import NanoPopup from "./NanoPopup";

/**
 * This class is a queue for popups. It will show the popups according to their priority.
 * If a popup needs to wait for a promise to be displayed, you can pass the promise to the Add method.
 */
export default class NanoPopupQueue {
  private queue: {
    popup: NanoPopup;
    priority: number;
    waitForPromise?: Promise<void>;
  }[] = [];
  private _suppressed = false;
  private onQueueEmpty: Array<() => void> = [];

  constructor() {
    // Will run this function when the document is ready
    $(() => {
      this.ShowNext();
    });
  }

  public static get instance(): NanoPopupQueue {
    if (!globalThis.popupQueue) {
      globalThis.popupQueue = new NanoPopupQueue();
    }
    return globalThis.popupQueue;
  }

  private _exclusivePopup?: NanoPopup = null;

  public get exclusivePopup(): NanoPopup {
    return this._exclusivePopup;
  }

  /**
   * An exclusive popup is shown once the queue is empty and not suppressed —
   * immediately on document ready if nothing is queued, otherwise after the
   * queued popups have all been dismissed.
   */
  public set exclusivePopup(value: NanoPopup) {
    this._exclusivePopup = value;
  }

  /**
   *
   * @param popup
   * @param priority The lower the number, the higher the priority
   * @param waitForPromise
   * @constructor
   */
  public Add(
    popup: NanoPopup,
    priority: number,
    waitForPromise: Promise<void> = null,
  ): void {
    const item = { popup, priority, waitForPromise };
    this.queue.push(item);
  }

  public ShowNext(): void {
    if (this._suppressed) {
      return;
    }
    if (this.queue.length === 0) {
      if (this._exclusivePopup) {
        const popup = this._exclusivePopup;
        this._exclusivePopup = null;
        popup.config.isHidden = () => {
          this.ShowNext();
        };
        popup.Show();
        return;
      }
      this.onQueueEmptyOnce();
      return;
    }
    this.queue.sort((a, b) => a.priority - b.priority);
    const next = this.queue.shift();
    const show = () => {
      next.popup.config.isHidden = () => {
        this.ShowNext();
      };
      next.popup.Show();
    };
    if (next.waitForPromise != null) {
      void next.waitForPromise.then(show);
      return;
    }
    show();
  }

  public OnQueueEmpty(callback: () => void): void {
    this.onQueueEmpty.push(callback);
  }

  /**
   * Suppress all popups. Moves current queue to suspended state and blocks ShowNext.
   * Use UnsuppressAll() to resume queue processing.
   */
  public SuppressAll(): void {
    if (this._suppressed) {
      return;
    }
    this._suppressed = true;
  }

  /**
   * Resume popup queue processing. Restores suspended popups and calls ShowNext.
   */
  public UnsuppressAll(): void {
    if (!this._suppressed) {
      return;
    }
    this._suppressed = false;
    this.ShowNext();
  }

  private onQueueEmptyOnce(): void {
    if (this.onQueueEmpty.length === 0) {
      return;
    }
    const callback = this.onQueueEmpty.reverse().pop();
    callback();
    this.onQueueEmptyOnce();
  }
}
